import { getLabel, type LabelKey } from "@/data/labels";

const AUTH_ERROR_CODE_MAP: Record<string, LabelKey> = {
  account_inactive: "SOCIAL_AUTH_ACCOUNT_INACTIVE",
  account_suspended: "SOCIAL_AUTH_ACCOUNT_SUSPENDED",
  account_deleted: "SOCIAL_AUTH_ACCOUNT_DELETED",
  staff_login_blocked: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
  // Legacy codes from older redirects — map to the same generic message.
  role_super_admin: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
  role_admin: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
  role_editor: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
  role_photographer: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
  role_reporter: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
  role_not_allowed: "SOCIAL_AUTH_STAFF_LOGIN_BLOCKED",
};

/** Map known English API / validation strings to label keys (Telugu via getLabel). */
const AUTH_MESSAGE_MAP: Array<{ includes: string; key: LabelKey }> = [
  { includes: "email must be a valid email", key: "VALID_EMAIL" },
  { includes: "please enter a valid email", key: "VALID_EMAIL" },
  { includes: "please enter a valid 10-digit phone number", key: "VALID_PHONE_10" },
  { includes: "please enter a valid 10-digit indian mobile", key: "VALID_PHONE_10" },
  { includes: "a user already exists", key: "USER_ALREADY_EXISTS" },
  { includes: "a user  already exists", key: "USER_ALREADY_EXISTS" },
  {
    includes: "password must be at least 8 characters and include uppercase",
    key: "PASSWORD_COMPLEXITY",
  },
  { includes: "at least one lowercase letter", key: "PASSWORD_RULE_LOWERCASE" },
  { includes: "at least one uppercase letter", key: "PASSWORD_RULE_UPPERCASE" },
  { includes: "at least one special character", key: "PASSWORD_RULE_SPECIAL" },
  { includes: "at least one digit", key: "PASSWORD_RULE_DIGIT" },
  { includes: "at least 8 characters long", key: "PASSWORD_RULE_MIN_8" },
  { includes: "registration service is unavailable", key: "REGISTRATION_SERVICE_UNAVAILABLE" },
  { includes: "registration failed", key: "REGISTRATION_FAILED" },
  { includes: "cannot connect to server", key: "CANNOT_CONNECT_SERVER" },
  { includes: "please enter a valid 6-digit otp", key: "PHONE_OTP_INVALID" },
  { includes: "invalid credentials", key: "INVALID_CREDENTIALS" },
  { includes: "phone number already exists", key: "PHONE_NUMBER_EXISTS" },
];

function translateSinglePart(message: string, lang: AppLocale): string {
  const trimmed = message.trim();
  const errorCode = AUTH_ERROR_CODE_MAP[trimmed];
  if (errorCode) {
    return getLabel(lang, errorCode);
  }

  const normalized = trimmed.toLowerCase();
  if (!normalized) return "";

  for (const { includes, key } of AUTH_MESSAGE_MAP) {
    if (normalized.includes(includes)) {
      return getLabel(lang, key);
    }
  }

  if (
    normalized.includes("lowercase") &&
    normalized.includes("uppercase") &&
    normalized.includes("special")
  ) {
    return getLabel(lang, "PASSWORD_COMPLEXITY");
  }

  return message.trim();
}

/**
 * Returns a localized auth/validation message for the current locale.
 * Falls back to the original text if no mapping is found.
 */
export function translateAuthMessage(message: string | undefined, lang: AppLocale): string {
  if (!message?.trim()) return "";

  const parts = message
    .split(/(?<=\.)\s+/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length > 1) {
    return parts.map((p) => translateSinglePart(p, lang)).join(" ");
  }

  return translateSinglePart(message, lang);
}

/** Localized message for social OAuth redirect `reason` query params. */
export function getSocialAuthErrorLabel(lang: AppLocale, reason: string | null | undefined): string {
  if (reason && AUTH_ERROR_CODE_MAP[reason]) {
    return getLabel(lang, AUTH_ERROR_CODE_MAP[reason]);
  }
  return getLabel(lang, "TRY_AGAIN");
}
